import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import ApiServices from "../../../../../Utils/ApiServices/ApiServices";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const SERVICE_TYPES = ["pickup", "delivery", "dine_in"];

const createDefaultSchedule = () => {
  const result = {};
  SERVICE_TYPES.forEach((serviceType) => {
    result[serviceType] = DAYS.map((day) => ({
      id: null,
      day,
      service_type: serviceType,
      open_time: "",
      close_time: "",
      break_start: "",
      break_end: "",
      is_closed: false,
    }));
  });
  return result;
};

const defaultQuickApplyForm = {
  open_time: "",
  close_time: "",
  break_start: "",
  break_end: "",
  is_closed: false,
};

const WorkingHoursSection = ({
  token,
  clientId,
  selectedLocation,
  locations,
  selectedLocationId,
  onSelectLocation,
  showSnackbar,
}) => {
  const [workingHours, setWorkingHours] = useState(createDefaultSchedule());
  const [workingHoursErrors, setWorkingHoursErrors] = useState({});
  const [savingHours, setSavingHours] = useState(false);
  const [selectedServiceTab, setSelectedServiceTab] = useState("pickup");
  const [quickApplyForm, setQuickApplyForm] = useState(defaultQuickApplyForm);
  const [copySourceDay, setCopySourceDay] = useState("monday");

  const mapWorkingHoursFromApi = (apiList = []) => {
    const base = createDefaultSchedule();

    apiList.forEach((item) => {
      const serviceType = item.service_type;
      const day = (item.day || "").toLowerCase();
      if (!base[serviceType]) return;

      const index = base[serviceType].findIndex((d) => d.day === day);
      if (index !== -1) {
        base[serviceType][index] = {
          id: item.id || null,
          day,
          service_type: serviceType,
          open_time: item.open_time || "",
          close_time: item.close_time || "",
          break_start: item.break_start || "",
          break_end: item.break_end || "",
          is_closed: !!item.is_closed,
        };
      }
    });

    return base;
  };

  const fetchWorkingHours = async () => {
    if (!selectedLocationId) {
      setWorkingHours(createDefaultSchedule());
      return;
    }

    try {
      const response = await ApiServices.get(
        `/api/v1/clients/${clientId}/locations/${selectedLocationId}/working-hours/`,
        { Authorization: `Bearer ${token}` }
      );
      const data = response?.data || response || [];
      setWorkingHours(mapWorkingHoursFromApi(Array.isArray(data) ? data : []));
      setWorkingHoursErrors({});
    } catch (error) {
      setWorkingHours(createDefaultSchedule());
      showSnackbar(error?.response?.data?.detail || "Failed to load working hours", "error");
    }
  };

  useEffect(() => {
    fetchWorkingHours();
  }, [selectedLocationId]);

  const validateSingleScheduleRow = (row) => {
    if (row.is_closed) return "";
    if (!row.open_time) return "Open time is required";
    if (!row.close_time) return "Close time is required";
    if (row.open_time >= row.close_time) return "Close time must be after open time";

    if (row.break_start && !row.break_end) return "Break end is required when break start is filled";
    if (!row.break_start && row.break_end) return "Break start is required when break end is filled";

    if (row.break_start && row.break_end) {
      if (row.break_start >= row.break_end) return "Break end must be after break start";
      if (row.break_start <= row.open_time || row.break_end >= row.close_time) {
        return "Break must be inside open and close time";
      }
    }

    return "";
  };

  const validateWorkingHours = () => {
    const errors = {};
    SERVICE_TYPES.forEach((serviceType) => {
      workingHours[serviceType].forEach((row) => {
        const error = validateSingleScheduleRow(row);
        if (error) errors[`${serviceType}-${row.day}`] = error;
      });
    });
    setWorkingHoursErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWorkingHourChange = (serviceType, day, field, value) => {
    setWorkingHours((prev) => ({
      ...prev,
      [serviceType]: prev[serviceType].map((row) => {
        if (row.day !== day) return row;

        if (field === "is_closed") {
          return {
            ...row,
            is_closed: value,
            open_time: value ? "" : row.open_time,
            close_time: value ? "" : row.close_time,
            break_start: value ? "" : row.break_start,
            break_end: value ? "" : row.break_end,
          };
        }

        return { ...row, [field]: value };
      }),
    }));

    setWorkingHoursErrors((prev) => ({
      ...prev,
      [`${serviceType}-${day}`]: "",
    }));
  };

  const handleQuickApplyInputChange = (field, value) => {
    setQuickApplyForm((prev) => {
      if (field === "is_closed") {
        return {
          ...prev,
          is_closed: value,
          open_time: value ? "" : prev.open_time,
          close_time: value ? "" : prev.close_time,
          break_start: value ? "" : prev.break_start,
          break_end: value ? "" : prev.break_end,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const applyTemplateToCurrentCategory = () => {
    const templateError = validateSingleScheduleRow({
      ...quickApplyForm,
      day: "template",
      service_type: selectedServiceTab,
    });

    if (templateError) {
      showSnackbar(templateError, "error");
      return;
    }

    setWorkingHours((prev) => ({
      ...prev,
      [selectedServiceTab]: prev[selectedServiceTab].map((row) => ({
        ...row,
        open_time: quickApplyForm.is_closed ? "" : quickApplyForm.open_time,
        close_time: quickApplyForm.is_closed ? "" : quickApplyForm.close_time,
        break_start: quickApplyForm.is_closed ? "" : quickApplyForm.break_start,
        break_end: quickApplyForm.is_closed ? "" : quickApplyForm.break_end,
        is_closed: quickApplyForm.is_closed,
      })),
    }));

    showSnackbar(`Applied same hours to all ${selectedServiceTab} days`);
  };

  const copyDayToCurrentCategory = (sourceDay) => {
    const sourceRow = workingHours[selectedServiceTab].find((r) => r.day === sourceDay);
    if (!sourceRow) return;

    const error = validateSingleScheduleRow(sourceRow);
    if (error) {
      showSnackbar(`Cannot copy from ${sourceDay}: ${error}`, "error");
      return;
    }

    setWorkingHours((prev) => ({
      ...prev,
      [selectedServiceTab]: prev[selectedServiceTab].map((row) => ({
        ...row,
        open_time: sourceRow.is_closed ? "" : sourceRow.open_time,
        close_time: sourceRow.is_closed ? "" : sourceRow.close_time,
        break_start: sourceRow.is_closed ? "" : sourceRow.break_start,
        break_end: sourceRow.is_closed ? "" : sourceRow.break_end,
        is_closed: sourceRow.is_closed,
      })),
    }));

    showSnackbar(`${sourceDay} copied to all days in ${selectedServiceTab}`);
  };

  const copyCurrentCategoryToAllCategories = () => {
    const currentRows = workingHours[selectedServiceTab] || [];
    const categoryErrors = currentRows.map(validateSingleScheduleRow).filter(Boolean);

    if (categoryErrors.length > 0) {
      showSnackbar("Fix current category errors before copying", "error");
      return;
    }

    setWorkingHours((prev) => {
      const sourceRows = prev[selectedServiceTab];
      const next = { ...prev };

      SERVICE_TYPES.forEach((serviceType) => {
        next[serviceType] = sourceRows.map((sourceRow) => ({
          ...prev[serviceType].find((r) => r.day === sourceRow.day),
          open_time: sourceRow.is_closed ? "" : sourceRow.open_time,
          close_time: sourceRow.is_closed ? "" : sourceRow.close_time,
          break_start: sourceRow.is_closed ? "" : sourceRow.break_start,
          break_end: sourceRow.is_closed ? "" : sourceRow.break_end,
          is_closed: sourceRow.is_closed,
        }));
      });

      return next;
    });

    showSnackbar("Current category copied to all categories");
  };

  const handleSaveWorkingHours = async () => {
    if (!selectedLocationId) {
      showSnackbar("Please select a location first", "error");
      return;
    }

    if (!validateWorkingHours()) {
      showSnackbar("Please fix working hours errors", "error");
      return;
    }

    const payload = {
      working_hours: SERVICE_TYPES.map((serviceType) => ({
        service_type: serviceType,
        schedule: workingHours[serviceType].map((row) => ({
          day: row.day,
          open_time: row.is_closed ? null : row.open_time || null,
          close_time: row.is_closed ? null : row.close_time || null,
          break_start: row.is_closed ? null : row.break_start || null,
          break_end: row.is_closed ? null : row.break_end || null,
          is_closed: !!row.is_closed,
        })),
      })),
    };

    setSavingHours(true);
    try {
      await ApiServices.post(
        `/api/v1/clients/${clientId}/locations/${selectedLocationId}/working-hours/`,
        payload,
        {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      );
      showSnackbar("Working hours saved successfully");
      fetchWorkingHours();
    } catch (error) {
      showSnackbar(error?.response?.data?.detail || "Failed to save working hours", "error");
    } finally {
      setSavingHours(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Location Working Hours
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedLocation
              ? `Manage working hours for ${selectedLocation.name}`
              : "Select a location to manage working hours"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            label="Selected Location"
            value={selectedLocationId}
            onChange={(e) => onSelectLocation(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            startIcon={savingHours ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSaveWorkingHours}
            disabled={!selectedLocationId || savingHours}
          >
            Save Working Hours
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Tabs
        value={selectedServiceTab}
        onChange={(_, val) => setSelectedServiceTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {SERVICE_TYPES.map((service) => (
          <Tab key={service} value={service} label={service.toUpperCase()} />
        ))}
      </Tabs>

      {!!selectedLocationId && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, backgroundColor: "action.hover" }}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Quick Apply Hours
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={quickApplyForm.is_closed}
                    onChange={(e) => handleQuickApplyInputChange("is_closed", e.target.checked)}
                  />
                }
                label="Closed"
              />
            </Grid>

            {["open_time", "close_time", "break_start", "break_end"].map((field) => (
              <Grid item xs={12} sm={6} md={2.5} key={field}>
                <TextField
                  type="time"
                  label={field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={quickApplyForm[field]}
                  disabled={quickApplyForm.is_closed}
                  onChange={(e) => handleQuickApplyInputChange(field, e.target.value)}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                <Button variant="contained" startIcon={<LibraryAddCheckIcon />} onClick={applyTemplateToCurrentCategory}>
                  Apply to all days in this category
                </Button>
                <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyCurrentCategoryToAllCategories}>
                  Copy this category to all categories
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Source Day"
                value={copySourceDay}
                onChange={(e) => setCopySourceDay(e.target.value)}
              >
                {DAYS.map((day) => (
                  <MenuItem key={day} value={day}>
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={9}>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copyDayToCurrentCategory(copySourceDay)}>
                Copy source day to all days in this category
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {selectedLocationId ? (
        <Stack spacing={2}>
          {workingHours[selectedServiceTab]?.map((row) => {
            const rowError = workingHoursErrors[`${selectedServiceTab}-${row.day}`];

            return (
              <Paper key={row.day} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <Typography variant="subtitle2" fontWeight={700} textTransform="capitalize">
                      {row.day}
                    </Typography>

                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          checked={row.is_closed}
                          onChange={(e) =>
                            handleWorkingHourChange(selectedServiceTab, row.day, "is_closed", e.target.checked)
                          }
                        />
                      }
                      label="Closed"
                    />
                  </Grid>

                  {["open_time", "close_time", "break_start", "break_end"].map((field) => (
                    <Grid item xs={12} md={2.5} key={field}>
                      <TextField
                        type="time"
                        label={field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={row[field]}
                        disabled={row.is_closed}
                        onChange={(e) =>
                          handleWorkingHourChange(selectedServiceTab, row.day, field, e.target.value)
                        }
                      />
                    </Grid>
                  ))}

                  {rowError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{rowError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Select a location first to manage working hours.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default WorkingHoursSection;